import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { decodeId } from '@/common/utils/encryption';
import { ConfirmDeleteModal, SkeletonCalendar } from '@/components';
import { useDelayedSkeleton } from '@/common/hooks/useDelayedSkeleton';
import { usePermissions } from '@/common/hooks/usePermissions';
import { PERMISSIONS } from '@/common/config/permissions';
import { parseApiError } from '@/common/utils';
import { DashboardPage } from '../../components/PremiumDesign';
import {
  CalendarLegend,
  CalendarToolbar,
  MonthView,
  TimeGridView,
  AppointmentDrawer,
} from '../components';
import {
  useCalendarAppointments,
  useAppointmentSettings,
  useCalendarStateSnapshot,
  useDeleteAppointment,
} from '../hooks';
import { getAppointmentDetail } from '../api';
import {
  addDaysToKey,
  addMonthsToKey,
  buildMonthGrid,
  buildWeekGrid,
  clampSlotToFuture,
  countByStatus,
  dayKeyToUtcDate,
  formatDayTitle,
  formatMonthTitle,
  formatWeekTitle,
  getWeekdayKey,
  groupEventsByDay,
  readAnyContactName,
  toDayKey,
  WEEKDAY_LABELS,
} from '../utils';

function getCompanyId() {
  try {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const userObj = JSON.parse(storedUser);
      return userObj?.company_id || localStorage.getItem('company_id');
    }
  } catch {
    // ignore
  }
  return localStorage.getItem('company_id');
}

export function AppointmentsPage() {
  const companyId = getCompanyId();
  const { can, role } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const baseCanCreate = can(PERMISSIONS.APPOINTMENTS.CREATE);
  const canUpdate = can(PERMISSIONS.APPOINTMENTS.UPDATE);
  const canDelete = can(PERMISSIONS.APPOINTMENTS.DELETE);
  const canViewSettings = role === 'owner';
  const canViewLeads = can(PERMISSIONS.LEADS.VIEW);

  const { initialState, saveState } = useCalendarStateSnapshot();
  const calendarScrollRef = useRef(null);
  const didRestoreScroll = useRef(false);

  const [view, setView] = useState(initialState.view || 'month');
  const [anchorKey, setAnchorKey] = useState(
    () => initialState.anchorKey || toDayKey(new Date()),
  );

  const todayKey = toDayKey(new Date());
  const isPastDayOpen = view === 'day' && anchorKey < todayKey;
  const canCreate = baseCanCreate && !isPastDayOpen;
  const [statusFilter, setStatusFilter] = useState(
    initialState.statusFilter || '',
  );
  const [searchQuery, setSearchQuery] = useState(
    initialState.searchQuery || '',
  );
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(
    initialState.debouncedSearchQuery || initialState.searchQuery || '',
  );

  // Debounce search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const [detailsDrawer, setDetailsDrawer] = useState({
    open: false,
    event: null,
  });
  const [formDrawer, setFormDrawer] = useState({
    open: false,
    mode: 'create',
    appointment: null,
    initialSlot: null,
  });
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Overlay close can leak a click onto the calendar. Ignore that leftover
  // pointer until the current event cycle finishes.
  const ignoreCalendarPointerRef = useRef(false);
  const armCalendarPointerGuard = useCallback(() => {
    ignoreCalendarPointerRef.current = true;
    window.setTimeout(() => {
      ignoreCalendarPointerRef.current = false;
    }, 0);
  }, []);

  const closeDetailsDrawer = useCallback(() => {
    armCalendarPointerGuard();
    setDetailsDrawer({ open: false, event: null });
  }, [armCalendarPointerGuard]);

  const closeFormDrawer = useCallback(() => {
    armCalendarPointerGuard();
    setFormDrawer({
      open: false,
      mode: 'create',
      appointment: null,
      initialSlot: null,
    });
  }, [armCalendarPointerGuard]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const encodedId = searchParams.get('appointment_id');
    if (!encodedId) return;

    const decoded = decodeId(encodedId);
    const appointmentId =
      decoded && !isNaN(Number(decoded))
        ? Number(decoded)
        : !isNaN(Number(encodedId))
          ? Number(encodedId)
          : null;

    if (!appointmentId || !companyId) return;

    const fetchAppointment = async () => {
      try {
        const detail = await queryClient.fetchQuery({
          queryKey: ['appointments', companyId, 'detail', appointmentId],
          queryFn: () => getAppointmentDetail({ companyId, appointmentId }),
        });

        if (detail) {
          setFormDrawer({
            open: false,
            mode: 'create',
            appointment: null,
            initialSlot: null,
          });
          setDetailsDrawer({
            open: true,
            event: detail,
          });
        }
      } catch (err) {
        console.error('Error fetching appointment details:', err);
        toast.error('Failed to load appointment details from notification.');
      } finally {
        searchParams.delete('appointment_id');
        const newSearch = searchParams.toString();
        navigate(
          {
            pathname: location.pathname,
            search: newSearch ? `?${newSearch}` : '',
          },
          { replace: true, state: location.state },
        );
      }
    };

    fetchAppointment();
  }, [
    location.search,
    location.pathname,
    location.state,
    navigate,
    companyId,
    queryClient,
  ]);

  const listParams = useMemo(() => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (debouncedSearchQuery) params.search = debouncedSearchQuery;
    return params;
  }, [statusFilter, debouncedSearchQuery]);

  const {
    data: rawEvents,
    isLoading: isCalendarLoading,
    isFetching: isCalendarFetching,
    isError,
    error,
  } = useCalendarAppointments(companyId, listParams);

  const events = useMemo(() => rawEvents || [], [rawEvents]);

  const showDelayedSkeleton = useDelayedSkeleton(
    isCalendarFetching && rawEvents !== undefined,
    300,
    500,
  );
  const isLoading = isCalendarLoading || showDelayedSkeleton;

  useEffect(() => {
    saveState({
      view,
      anchorKey,
      statusFilter,
      searchQuery,
      debouncedSearchQuery,
    });
  }, [
    view,
    anchorKey,
    statusFilter,
    searchQuery,
    debouncedSearchQuery,
    saveState,
  ]);

  useEffect(() => {
    const el = calendarScrollRef.current;
    if (!el || isLoading) return undefined;
    let rafId = null;
    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        saveState({
          gridScrollTop: el.scrollTop,
          gridScrollLeft: el.scrollLeft,
        });
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [isLoading, view, saveState]);

  useLayoutEffect(() => {
    if (didRestoreScroll.current) return;
    if (isLoading || !rawEvents) return;

    const grid = calendarScrollRef.current;
    const dash = document.getElementById('dashboard-scroll-container');
    if (grid) {
      grid.scrollTop = initialState.gridScrollTop || 0;
      grid.scrollLeft = initialState.gridScrollLeft || 0;
    }
    if (dash && initialState.dashScrollTop) {
      dash.scrollTop = initialState.dashScrollTop;
    }
    didRestoreScroll.current = true;
  }, [
    isLoading,
    rawEvents,
    view,
    initialState.gridScrollTop,
    initialState.gridScrollLeft,
    initialState.dashScrollTop,
  ]);

  const { data: settings } = useAppointmentSettings(companyId);
  const deleteMutation = useDeleteAppointment(companyId);

  const eventsByDay = useMemo(() => groupEventsByDay(events), [events]);

  const monthCells = useMemo(
    () => (view === 'month' ? buildMonthGrid(anchorKey) : []),
    [view, anchorKey],
  );

  const weekDays = useMemo(() => {
    if (view === 'week') return buildWeekGrid(anchorKey);
    if (view === 'day') {
      const grid = buildWeekGrid(anchorKey);
      const match = grid.find((x) => x.key === anchorKey);
      if (match) return [match];
      const d = dayKeyToUtcDate(anchorKey);
      return [
        {
          key: anchorKey,
          day: d?.getUTCDate(),
          isToday: anchorKey === toDayKey(new Date()),
          weekdayKey: getWeekdayKey(anchorKey),
          label: WEEKDAY_LABELS[getWeekdayKey(anchorKey)],
        },
      ];
    }
    return [];
  }, [view, anchorKey]);

  const visibleEvents = useMemo(() => {
    if (view === 'month') {
      return monthCells
        .filter((c) => c.inMonth)
        .flatMap((c) => eventsByDay[c.key] || []);
    }
    return weekDays.flatMap((c) => eventsByDay[c.key] || []);
  }, [view, monthCells, weekDays, eventsByDay]);

  const counts = useMemo(() => countByStatus(visibleEvents), [visibleEvents]);

  const rangeLabel =
    view === 'month'
      ? formatMonthTitle(anchorKey)
      : view === 'week'
        ? formatWeekTitle(anchorKey)
        : formatDayTitle(anchorKey);

  const summaryText = `${counts.total} appointment${counts.total === 1 ? '' : 's'} in ${
    view === 'day'
      ? new Intl.DateTimeFormat('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          timeZone: 'UTC',
        }).format(dayKeyToUtcDate(anchorKey) || new Date())
      : rangeLabel
  }`;

  const goPrev = () => {
    if (view === 'month') setAnchorKey((k) => addMonthsToKey(k, -1));
    else if (view === 'week') setAnchorKey((k) => addDaysToKey(k, -7));
    else setAnchorKey((k) => addDaysToKey(k, -1));
  };

  const goNext = () => {
    if (view === 'month') setAnchorKey((k) => addMonthsToKey(k, 1));
    else if (view === 'week') setAnchorKey((k) => addDaysToKey(k, 7));
    else setAnchorKey((k) => addDaysToKey(k, 1));
  };

  const openCreateSlot = (dayKey, startMinutes) => {
    if (!canCreate) return;
    const clamped = clampSlotToFuture(dayKey, startMinutes);
    if (!clamped) {
      toast.error('Cannot book in the past');
      return;
    }
    setDetailsDrawer({ open: false, event: null });
    setFormDrawer({
      open: true,
      mode: 'create',
      appointment: null,
      initialSlot: clamped,
    });
  };

  const openCreateFromCalendar = (dayKey, startMinutes) => {
    if (ignoreCalendarPointerRef.current) return;
    if (detailsDrawer.open || formDrawer.open) return;
    openCreateSlot(dayKey, startMinutes);
  };

  const handleEventClick = (event) => {
    if (ignoreCalendarPointerRef.current) return;
    if (detailsDrawer.open || formDrawer.open) return;
    setFormDrawer({
      open: false,
      mode: 'create',
      appointment: null,
      initialSlot: null,
    });
    setDetailsDrawer({
      open: true,
      event,
    });
  };

  const handleContactClick = useCallback(
    (contactId, contactName) => {
      if (!contactId || !canViewLeads) return;
      const id = String(contactId);
      const name = contactName || '';
      closeDetailsDrawer();
      closeFormDrawer();
      const dash = document.getElementById('dashboard-scroll-container');
      const grid = calendarScrollRef.current;
      saveState({
        view,
        anchorKey,
        statusFilter,
        searchQuery,
        debouncedSearchQuery,
        gridScrollTop: grid?.scrollTop || 0,
        gridScrollLeft: grid?.scrollLeft || 0,
        dashScrollTop: dash?.scrollTop || 0,
      });
      try {
        sessionStorage.setItem('crm-contact-overview-id', id);
        sessionStorage.setItem('crm-contact-overview-name', name);
        window.dispatchEvent(new Event('crm-contact-overview-updated'));
      } catch {
        // ignore storage errors
      }
      navigate('/crm-contacts/overview', {
        state: {
          contactId: id,
          contactName: name,
          from: location.pathname + location.search,
        },
      });
    },
    [
      canViewLeads,
      closeDetailsDrawer,
      closeFormDrawer,
      saveState,
      view,
      anchorKey,
      statusFilter,
      searchQuery,
      debouncedSearchQuery,
      navigate,
      location.pathname,
      location.search,
    ],
  );

  const handleEdit = (appointment) => {
    setDetailsDrawer({ open: false, event: null });
    setFormDrawer({
      open: true,
      mode: 'edit',
      appointment,
      initialSlot: null,
    });
  };

  const handleDeleteRequest = (appointment) => {
    closeDetailsDrawer();
    closeFormDrawer();
    setDeleteTarget(appointment);
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success('Appointment deleted');
      setDeleteTarget(null);
    } catch (err) {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to delete appointment');
    }
  };

  const deleteLabel =
    readAnyContactName(deleteTarget?.contact) ||
    deleteTarget?.contact_name ||
    'this appointment';

  return (
    <DashboardPage className="overflow-y-auto lg:!overflow-hidden !bg-transparent !p-0 sm:!p-0">
      <div className="appointments-calendar flex flex-col min-h-full lg:h-full gap-2 sm:gap-3 pb-3 lg:pb-0">
        {isError && (
          <div className="shrink-0 rounded-2xl border border-red-200 bg-red-50 px-3 sm:px-4 py-3 text-sm text-red-700">
            {parseApiError(error).message || 'Failed to load appointments.'}
          </div>
        )}

        <div className="flex-1 min-h-fit lg:min-h-0 bg-white border border-[#EBF4EA] rounded-xl sm:rounded-2xl flex flex-col overflow-visible lg:overflow-hidden">
          <div className="shrink-0 p-3 sm:p-4 lg:px-5 border-b border-[#EBF4EA]">
            <CalendarToolbar
              view={view}
              onViewChange={setView}
              anchorKey={anchorKey}
              onPrev={goPrev}
              onNext={goNext}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              canCreate={canCreate}
              canViewSettings={canViewSettings}
              onNewAppointment={() => openCreateSlot(toDayKey(new Date()))}
              onToday={() => setAnchorKey(toDayKey(new Date()))}
              searchQuery={searchQuery}
              onSearchQueryChange={setSearchQuery}
            />
          </div>

          {isLoading || !rawEvents ? (
            <div className="p-3 sm:p-4 flex-1 min-h-0 flex flex-col">
              <SkeletonCalendar view={view} />
            </div>
          ) : view === 'month' ? (
            <MonthView
              cells={monthCells}
              eventsByDay={eventsByDay}
              settings={settings}
              canCreate={canCreate}
              scrollRef={calendarScrollRef}
              onDayClick={(key) => {
                if (ignoreCalendarPointerRef.current) return;
                if (detailsDrawer.open || formDrawer.open) return;
                setAnchorKey(key);
                setView('day');
              }}
              onSlotCreate={(key) => openCreateFromCalendar(key)}
              onEventClick={handleEventClick}
            />
          ) : (
            <TimeGridView
              days={weekDays}
              eventsByDay={eventsByDay}
              settings={settings}
              canCreate={canCreate}
              scrollRef={calendarScrollRef}
              onSlotCreate={openCreateFromCalendar}
              onEventClick={handleEventClick}
            />
          )}

          <div className="shrink-0 px-3 sm:px-5 pb-3 pt-0 bg-white relative z-[1]">
            <CalendarLegend counts={counts} summaryText={summaryText} />
          </div>
        </div>
      </div>

      <AppointmentDrawer
        isOpen={detailsDrawer.open}
        onClose={closeDetailsDrawer}
        mode="details"
        companyId={companyId}
        event={detailsDrawer.event}
        canUpdate={canUpdate}
        canDelete={canDelete}
        canUpdateStatus={canUpdate}
        onEdit={handleEdit}
        onDelete={handleDeleteRequest}
        onContactClick={canViewLeads ? handleContactClick : undefined}
      />

      <AppointmentDrawer
        isOpen={formDrawer.open}
        onClose={closeFormDrawer}
        mode={formDrawer.mode}
        companyId={companyId}
        appointment={formDrawer.appointment}
        initialSlot={formDrawer.initialSlot}
        canUpdate={canUpdate}
        canDelete={canDelete}
        canUpdateStatus={canUpdate}
      />

      <ConfirmDeleteModal
        isOpen={!!deleteTarget}
        title="Delete Appointment"
        itemName={deleteLabel}
        description="This appointment will be permanently removed from the calendar."
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        isPending={deleteMutation.isPending}
      />
    </DashboardPage>
  );
}
